"use client";

import { Button } from "@/components/atoms/button";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Input } from "@/components/atoms/input";

import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { useHasHydrated } from "@/hooks/useHasHydrated/useHasHydrated";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { RegionListQuery } from "@/types/generated";
import { cityAndHotelFuzzyFilter } from "@/utils";
import { Maybe } from "graphql/jsutils/Maybe";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
type CancellationFormValues = {
  hotel: string;
  reservationCode: string;
  email: string;
};
type CancellationFormProps = {
  cancellationFormData?: ICancellationFormData | null;
  locale: string;
  regionData: RegionListQuery;
};
interface ICancellationFormData {
  cancellation_fee_apply_text: string | null;
  cancellation_fee_apply_link: string | null;
  select_hotel_placeholder: string | null;
  reservation_code_placeholder: string | null;
  invalid_reservation_code_message: string | null;
  email_placeholder: string | null;
  invalid_email_messager: string | null;
  confirm_button_text: string | null;
  documentId: string;
}

interface IOptionSelect {
  value: string | null;
  label: string;
  data: {
    cityId: string | null;
    citySearchNames: Maybe<string>[];
    order: number | null;
    searchNames: string[];
    type: string;
  };
}

const inputTransformStyle = {
  transformOrigin: "left center",
  transform: "scale(0.75)",
  width: "133.33%",
};

/**
 * CancellationForm component
 *
 * Handles hotel reservation cancellation through Tripla booking system.
 * Dynamically builds hotel options, validates input (reservation code & email),
 * and opens Tripla's cancellation URL on valid submission.
 */
const CancellationForm: React.FC<CancellationFormProps> = ({
  cancellationFormData,
  locale,
  regionData,
}) => {
  const { japanAreas, allLocaleHotels } = useJapanAreasStore();

  // Build the hotel options list dynamically from Japan area and region data
  const hotelOptions: IOptionSelect[] = useMemo(() => {
    if (!allLocaleHotels || !japanAreas) return [];

    // Helper function to find hotel order
    const findHotelOrder = (
      selectedHotels:
        | { order: number; documentId: string }[]
        | undefined
        | null,
      hotelDocumentId: string | undefined | null,
    ): number | null => {
      if (!selectedHotels || !hotelDocumentId) return null;
      return (
        selectedHotels.find((item) => item.documentId === hotelDocumentId)
          ?.order ?? null
      );
    };

    // Map of hotel document IDs to an array of searchable names
    const searchNames = new Map();
    const hotels = Object.values(allLocaleHotels).filter(Array.isArray).flat();

    for (const h of hotels) {
      const hotel = h as {
        documentId?: string | null;
        properties?: { hotel_name?: string | null };
        item_name?: string | null;
        display_name?: string | null;
      };

      if (hotel?.documentId) {
        const names = searchNames.get(hotel.documentId) || [];
        searchNames.set(
          hotel.documentId,
          Array.from(
            new Set(
              [
                ...names,
                hotel.properties?.hotel_name,
                hotel.item_name,
                hotel.display_name,
              ].filter(Boolean),
            ),
          ),
        );
      }
    }

    // Create list of hotels with tripla booking codes
    const japanHotelList = japanAreas.flatMap((area) => {
      const hotelsWithTriplaCode = (area.hotels || []).filter(
        (h) => h?.external_ids?.tripla_booking_code,
      );

      return hotelsWithTriplaCode.map((h) => {
        const hotelOrder = findHotelOrder(area?.selected_hotels, h!.documentId);

        return {
          value: h!.external_ids!.tripla_booking_code,
          label: h!.properties?.hotel_name || "",
          data: {
            type: "hotel",
            searchNames: searchNames.get(h!.documentId) || [],
            citySearchNames: [area?.item_name, area?.area_name].filter(Boolean),
            cityId: area.documentId,
            order: hotelOrder,
          },
        };
      });
    });

    // Use regionData to determine display order of hotels based on region & city
    const selectedRegions =
      regionData?.datasourceJapans[0]?.region_datasource?.selected_regions ??
      [];

    const sortedSelectedRegions = [...selectedRegions].sort(
      (a, b) => a.order - b.order,
    );
    const newSortDataHotel: IOptionSelect[] = [];

    // Sort cities inside each region and push hotels sorted by their defined order
    for (const { documentId } of sortedSelectedRegions ?? []) {
      const currentRegion =
        regionData?.datasourceJapans[0]?.region_datasource?.regions.find(
          (region) => region?.documentId === documentId,
        );

      if (
        !currentRegion ||
        !Array.isArray(currentRegion.cities_order_on_top_page)
      ) {
        continue;
      }

      const currentCityList = [...currentRegion.cities_order_on_top_page].sort(
        (a, b) => a.order - b.order,
      );

      for (const cityItem of currentCityList) {
        const matchingHotelWithCurrentCity = japanHotelList
          .filter(
            (item) =>
              item.data.order && item.data.cityId === cityItem.documentId,
          )
          .sort((a, b) => (a.data.order ?? 0) - (b.data.order ?? 0));
        newSortDataHotel.push(...matchingHotelWithCurrentCity);
      }
    }

    return newSortDataHotel;
  }, [allLocaleHotels, japanAreas, regionData]);
  const hasHydrated = useHasHydrated();

  const searchParams = useSearchParams();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CancellationFormValues>({
    defaultValues: {
      hotel: "",
      reservationCode: "",
      email: "",
    },
  });

  // Prefill hotel field if cancelhotelid is present in URL
  useEffect(() => {
    const cancelHotelId = searchParams.get("cancelhotelid");
    if (cancelHotelId && hotelOptions.length > 0) {
      const matchingHotel = hotelOptions.find(
        (option) => option.value === cancelHotelId,
      );
      if (matchingHotel) {
        setValue("hotel", matchingHotel.value || "");
      }
    }
  }, [searchParams, hotelOptions, setValue]);
  const hotel = useWatch({ control, name: "hotel" });
  const reservationCode = useWatch({ control, name: "reservationCode" });
  const emailValue = useWatch({ control, name: "email" });
  //validations
  const isValidReservationCode = useMemo(() => {
    if (!reservationCode) return false;
    const code = reservationCode.toLowerCase();
    return code.startsWith("t") && !code.startsWith("ty") && code.length === 13;
  }, [reservationCode]);

  const isValidEmail = useMemo(() => {
    const regex = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
    return regex.test(emailValue);
  }, [emailValue]);

  // Open Tripla cancellation URL when inputs are valid
  const onSubmit = (data: CancellationFormValues) => {
    if (hotel && isValidReservationCode && isValidEmail) {
      const triplaBookingCode = hotel;
      const reservationCode = data?.reservationCode;

      const url = createTriplaBookingHotelUrl(
        triplaBookingCode,
        reservationCode,
        locale,
        data.email,
      );
      window.open(url, "_blank");
    }
  };

  // Helper function to build Tripla cancellation URL
  const createTriplaBookingHotelUrl = (
    triplaBookingCode: string,
    reservationCode: string,
    locale: string,
    email: string,
  ): string => {
    const normalizedLocale = locale === "en-us" ? "en" : locale;
    return `${TRIPLA_BOOKING_URL}/reservation/confirmation/${reservationCode}?code=${triplaBookingCode}&locale=${normalizedLocale}&email=${email}`;
  };

  return (
    <>
      {hasHydrated && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full max-w-[100%]">
            <div className="md:max-w-[380px]">
              <Controller
                control={control}
                name="hotel"
                defaultValue={""}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    styles={{
                      placeholder: (base) => ({
                        ...base,
                        color: "var(--color-neutral-gray)",
                        fontSize: "12px",
                        fontWeight: "500",
                      }),
                      control: (base, state) => ({
                        ...base,
                        border: "2px solid var(--fill-gray-lighter)",
                        borderRadius: 0,
                        padding: "10px",
                        "&:hover": {
                          borderColor: "var(--fill-gray-lighter)",
                        },
                        "&:focus": {
                          borderColor: "var(--fill-gray-lighter)",
                        },
                        cursor: "text",
                        height: "60px",
                        marginBottom: "20px",
                        boxShadow: state.isFocused ? "none" : base.boxShadow,
                        borderColor: state.isFocused
                          ? "var(--fill-gray-lighter)"
                          : base.borderColor,
                      }),
                      option: (base, state) => {
                        let backgroundColor = "white";
                        if (state.isSelected) {
                          backgroundColor = "var(--color-secondary-400)";
                        } else if (state.isFocused) {
                          backgroundColor = "var(--color-secondary-300)";
                        }

                        return {
                          ...base,
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "var(--color-neutral-gray)",
                          cursor: "pointer",
                          backgroundColor,
                        };
                      },
                      singleValue: (base, _state) => ({
                        ...base,
                        padding: 0,
                        fontSize: "12px",
                        color: "var(--color-neutral-gray)",
                        fontWeight: "400",
                      }),
                      dropdownIndicator: (base, _state) => ({
                        ...base,
                        cursor: "pointer",
                      }),
                      input: (base) => ({
                        ...base,
                        color: "var(--text-base-primary)",
                        fontSize: "16px",
                        ...inputTransformStyle,
                      }),
                    }}
                    options={hotelOptions}
                    placeholder={
                      cancellationFormData?.select_hotel_placeholder || ""
                    }
                    isMulti={false}
                    value={hotelOptions.find((o) => o?.value === field?.value)}
                    onChange={(selected) =>
                      field.onChange(selected?.value || null)
                    }
                    // Use fuzzy filter to match both city and hotel names
                    filterOption={(option, inputValue) => {
                      return cityAndHotelFuzzyFilter(
                        {
                          label: option.label,
                          data: option.data?.data,
                        },
                        inputValue,
                      );
                    }}
                  />
                )}
              />
            </div>

            {/* Show reservation code input after hotel is selected */}
            {hotel && (
              <div className="mb-[20px] md:max-w-[380px]">
                <Controller
                  control={control}
                  name="reservationCode"
                  rules={{
                    required: `${cancellationFormData?.invalid_reservation_code_message || ""}`,
                    validate: (value) => {
                      const code = value.toLowerCase();
                      if (
                        code.startsWith("t") &&
                        !code.startsWith("ty") &&
                        code.length === 13
                      )
                        return true;
                      return (
                        cancellationFormData?.invalid_reservation_code_message ||
                        ""
                      );
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      className="rounded-none border-2 border-[var(--fill-gray-lighter)] px-[10px] py-6 text-base font-normal text-[var(--color-neutral-gray)] focus:shadow-none focus:outline-none active:!shadow-none"
                      {...field}
                      placeholder={
                        cancellationFormData?.reservation_code_placeholder || ""
                      }
                      error={errors.reservationCode?.message}
                      style={inputTransformStyle}
                    />
                  )}
                />
              </div>
            )}

            <div className="flex w-full flex-col md:flex-row md:items-center md:gap-2">
              {/* Show email input only when reservation code is valid */}
              {isValidReservationCode && (
                <div className="mb-3 min-h-[80px] w-full md:mb-0 md:w-[380px]">
                  <Controller
                    control={control}
                    name="email"
                    rules={{
                      required: `${cancellationFormData?.invalid_email_messager || ""}`,
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/,
                        message: `${cancellationFormData?.invalid_email_messager || ""}`,
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        className="h-[60px] w-full rounded-none border-2 border-[var(--fill-gray-lighter)] px-[10px] py-[6px] text-[12px] font-normal text-[var(--color-neutral-gray)] focus:shadow-none focus:outline-none active:!shadow-none"
                        {...field}
                        placeholder={
                          cancellationFormData?.email_placeholder || ""
                        }
                        error={errors.email?.message}
                      />
                    )}
                  />
                </div>
              )}

              {/* Show confirm button when reservation code is entered */}
              {!!reservationCode?.length && (
                <div className="min-h-[80px]">
                  <Button
                    variant="secondary"
                    size="xl"
                    type="submit"
                    className="h-[60px] text-[15px] font-extrabold"
                  >
                    {cancellationFormData?.confirm_button_text || ""}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default CancellationForm;
